import { API_BASE, authHeaders } from './apiAuth'
import { decodeRefs, type ImageRef } from './imageRefs'

/**
 * Chat, through the API Worker
 * -------------------------------------------------------------
 * The browser never calls OpenAI. It calls our Worker, which holds the key,
 * remembers the conversation and records what every call cost. What comes back
 * is our own two-event stream — a piece of text, then a completion — so nothing
 * here is shaped by OpenAI's wire format and a change at their end is a change
 * in the Worker alone.
 */

export interface ChatModel {
  slug: string
  label: string
  note: string
  input_usd_per_mtok: number
  output_usd_per_mtok: number
  /**
   * Where this person's conversations start. Decided by the Worker, which
   * knows whether they are an administrator; the browser only obeys it.
   */
  isDefault: boolean
}

/**
 * A document sent in a conversation (2026-10-14): kept by the Worker in R2,
 * named here by its key. Sent to the model whole.
 */
export interface ChatFile {
  key: string
  name: string
  mime: string
  size: number
}

/** The documents a conversation can take, by extension. */
export const CHAT_FILE_TYPES = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'md', 'csv', 'json']

export const isChatFile = (file: File): boolean =>
  CHAT_FILE_TYPES.includes((file.name.split('.').pop() ?? '').toLowerCase())

/** Keep a document for a conversation, before the message that carries it is sent. */
export async function uploadChatFile(projectId: string, file: File): Promise<ChatFile> {
  const response = await fetch(`${API_BASE}/api/ai/files?project=${encodeURIComponent(projectId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': encodeURIComponent(file.name),
      ...(await authHeaders('web')),
    },
    body: file,
  })
  if (!response.ok) throw new Error(await reason(response))
  return (await response.json()) as ChatFile
}

const readChatFiles = (value: unknown): ChatFile[] =>
  Array.isArray(value)
    ? (value as Partial<ChatFile>[]).filter(
        (one): one is ChatFile => typeof one?.key === 'string' && typeof one?.name === 'string',
      )
    : []

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  body: string
  model: string
  /** Names of pictures sent with it, not links — links expire. */
  images: ImageRef[]
  /** Documents sent with it. */
  files: ChatFile[]
  created_at: string
  /** Who wrote it, for a user message; empty for the assistant's. */
  author_id?: string | null
  author_name?: string
}

/** One hand-over of a shared project: whose it was, whose it became, when. */
export interface Handover {
  from: string
  to: string
  at: string
}

/** A picture run still being drawn for one of a conversation's messages. */
export interface PendingRun {
  runId: string
  messageId: string
  /** How many pictures it is making -- one empty frame each. */
  n: number
}

export interface Conversation {
  id: string
  title: string
  messages: ChatMessage[]
  /** Runs not finished when the conversation was read. Empty from an older Worker. */
  pending: PendingRun[]
  /** Oldest first. Empty for a project nobody shared. */
  handovers: Handover[]
  /** Which of the project's conversations this is; null for its first (2026-10-22). */
  thread: string | null
  /** Every conversation of the project, its first one first (id null). */
  threads: ChatThread[]
}

/** One of a project's conversations (docs/chat-threads.sql). The first one has no id. */
export interface ChatThread {
  id: string | null
  title: string
  created_at: string | null
}

export interface ConversationSummary {
  id: string
  title: string
  created_at: string
  updated_at: string
  /** The name of whoever shared it with this person, when somebody did. */
  shared_by?: string | null
  shared_at?: string | null
  /** When it was first opened. Empty on a shared one: not yet. */
  seen_at?: string | null
  /** When it was last opened, for the overview's order. */
  opened_at?: string | null
  /**
   * The card's look a shared copy came with (docs/project-card-look.sql,
   * 2026-10-26): shown until the person chooses their own.
   */
  card_look?: { colour?: string; picture?: string } | null
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { Accept: 'application/json', ...(await authHeaders('web')) },
  })
  if (!response.ok) throw new Error(await reason(response))
  return (await response.json()) as T
}

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    if (typeof body.error === 'string' && body.error) return body.error
  } catch {
    /* fall through to the status */
  }
  return `Something went wrong (HTTP ${response.status})`
}

export function listModels(): Promise<ChatModel[]> {
  return get<ChatModel[]>('/api/ai/models')
}

export function listConversations(): Promise<ConversationSummary[]> {
  return get<ConversationSummary[]>('/api/ai/conversations')
}

export async function readConversation(id: string, thread?: string | null): Promise<Conversation> {
  const body = await get<
    Omit<Conversation, 'messages' | 'pending' | 'thread' | 'threads'> & {
      messages: unknown[]
      pending?: unknown
      thread?: unknown
      threads?: unknown
    }
  >(`/api/ai/conversation?id=${encodeURIComponent(id)}${thread ? `&thread=${encodeURIComponent(thread)}` : ''}`)

  // DECODED HERE, WHICH IS THE WHOLE POINT OF THIS FUNCTION EXISTING
  //
  // The server stores and returns picture names as strings — `project:19ab…`.
  // `ChatMessage.images` is typed as the decoded pair, and for a long time
  // nothing turned one into the other: `get<T>()` is an assertion, not a
  // check, so TypeScript was told a shape the data had never had and could not
  // warn about it.
  //
  // What that looked like: every picture in a reopened conversation silently
  // vanished. Reading `.store` off a string gives undefined, the lookup misses,
  // and the code that filters out pictures whose links could not be found
  // filtered out all of them. Live it was fine, because the streaming path
  // builds the pair itself — so it only showed on reload, which is exactly the
  // moment somebody is checking that their work is still there.
  return {
    ...body,
    thread: typeof body.thread === 'string' ? body.thread : null,
    threads: Array.isArray(body.threads)
      ? (body.threads as Partial<ChatThread>[])
          .filter((one) => one && (one.id === null || typeof one.id === 'string'))
          .map((one) => ({
            id: one.id ?? null,
            title: typeof one.title === 'string' ? one.title : '',
            created_at: typeof one.created_at === 'string' ? one.created_at : null,
          }))
      : [{ id: null, title: '', created_at: null }],
    handovers: Array.isArray((body as { handovers?: unknown }).handovers)
      ? (body as { handovers: Partial<Handover>[] }).handovers.filter(
          (one): one is Handover =>
            typeof one.from === 'string' && typeof one.to === 'string' && typeof one.at === 'string',
        )
      : [],
    // Checked rather than asserted, for the reason above.
    pending: Array.isArray(body.pending)
      ? (body.pending as Partial<PendingRun>[]).filter(
          (one): one is PendingRun =>
            typeof one.runId === 'string' &&
            typeof one.messageId === 'string' &&
            typeof one.n === 'number',
        )
      : [],
    messages: (
      body.messages as (Omit<ChatMessage, 'images' | 'files'> & { images?: unknown; files?: unknown })[]
    ).map((message) => ({
      ...message,
      images: decodeRefs(message.images),
      files: readChatFiles(message.files),
    })),
  }
}

/**
 * Start an empty conversation.
 *
 * Called the first time anything is put on a project's canvas, so that a board
 * arranged before a word is typed has somewhere to be kept.
 */
export async function createConversation(): Promise<{ id: string; title: string }> {
  const response = await fetch(`${API_BASE}/api/ai/conversation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders('web')) },
    body: '{}',
  })
  if (!response.ok) throw new Error(await reason(response))
  return (await response.json()) as { id: string; title: string }
}

/** Give a conversation a different name. Returns the name as stored. */
export async function renameConversation(id: string, title: string): Promise<string> {
  const response = await fetch(`${API_BASE}/api/ai/conversation?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders('web')) },
    body: JSON.stringify({ title }),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { title?: string }
  return typeof body.title === 'string' ? body.title : title
}

/**
 * Rename one of a project's conversations (2026-10-22). `thread` null is the
 * first one; an empty name puts it back to "Main chat". Returns the name as
 * stored.
 */
export async function renameThread(projectId: string, thread: string | null, title: string): Promise<string> {
  const query = `id=${encodeURIComponent(projectId)}${thread ? `&thread=${encodeURIComponent(thread)}` : ''}`
  const response = await fetch(`${API_BASE}/api/ai/thread?${query}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders('web')) },
    body: JSON.stringify({ title }),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { title?: string }
  return typeof body.title === 'string' ? body.title : title
}

/**
 * Delete a conversation and everything said in it. Permanent.
 *
 * What is not deleted is the record of what it cost: usage events keep their
 * own copy of the model, the tokens and the price, and only lose the link to
 * the conversation. Tidying a workspace should not edit a bill.
 */
export async function deleteConversation(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/ai/conversation?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...(await authHeaders('web')) },
  })
  if (!response.ok) throw new Error(await reason(response))
}

export interface SendOptions {
  message: string
  /** Pictures to send with it. Already uploaded; named, not attached. */
  images?: ImageRef[]
  /** Documents to send with it. Already uploaded (uploadChatFile). */
  files?: ChatFile[]
  /** The model has started a web search. */
  onSearching?: () => void
  /** A piece of the model's summary of its own thinking. */
  onThinking?: (piece: string) => void
  /** Omitted for the first message, which is what creates the conversation. */
  projectId?: string
  model?: string
  /** Called with each piece of the answer as it arrives. */
  onDelta: (piece: string) => void
  /**
   * Called when the model asks for a picture, before it has been queued.
   *
   * The call id arrives in the very first event of a call, forty events before
   * its arguments finish. This is that gap, and it is where the empty frame
   * goes -- without it the answer simply stops, which is indistinguishable
   * from it having failed.
   */
  onPictureStart?: () => void
  /**
   * Called once a picture has actually been queued, with the run to watch.
   *
   * The picture is no longer drawn inside the answer's own stream: that is
   * what spent the turn's hundred seconds against Cloudflare's ceiling and
   * lost the connection after the money was gone. The turn ends with words and
   * the run finishes on its own.
   */
  onPictureQueued?: (runId: string, count: number) => void
  /** Called when a picture could not even be queued, with the reason. */
  onPictureFailed?: (reason: string) => void
  /**
   * Called once, before the model is asked, with the conversation's id.
   *
   * Separate from the result on purpose: by this point the message has been
   * saved, so the browser must be able to find its way back even if the answer
   * never comes.
   */
  onStart?: (projectId: string, threadId: string | null) => void
  /** Which of the project's conversations: an id, 'new' to start one, or nothing for its first. */
  threadId?: string | null
  signal?: AbortSignal
  /**
   * The line was lost and the answer was found saved on the server instead
   * (2026-11-03): the whole answer, to show in place of what had arrived,
   * and the pictures already saved on it. A picture whose run finished before
   * the answer was found is no longer in `runs`, so without these it would
   * only appear when the conversation is opened again.
   */
  onRecovered?: (answer: string, images: ImageRef[]) => void
}

/** Silence this long (the server beats every 10 s) means the line is gone. */
const SILENT_MS = 30_000
/** How long to look for the saved answer once the line is gone, and how often. */
const RECOVER_FOR_MS = 4 * 60_000
const RECOVER_EVERY_MS = 3_000

export interface SendResult {
  projectId: string
  model: string
  /**
   * The assistant's message, saved empty of pictures.
   *
   * The pictures this turn asked for are still being made. The server puts
   * them on this message itself when each run finishes, tab open or not; watch
   * the runs below only to show them the moment they land.
   */
  messageId: string
  /**
   * The picture runs this turn queued, with how many each is making.
   *
   * The count is here because the conversation draws an empty frame per
   * picture while it waits, and one call asking for ten is one run.
   */
  runs: { id: string; n: number }[]
}

/**
 * Send a message and stream the answer back.
 *
 * `fetch` rather than `EventSource`, because this is a POST carrying an
 * authorisation header and EventSource can do neither.
 */
export async function sendMessage(options: SendOptions): Promise<SendResult> {
  const response = await fetch(`${API_BASE}/api/ai/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders('web')) },
    body: JSON.stringify({
      message: options.message,
      images: options.images ?? [],
      files: options.files ?? [],
      projectId: options.projectId,
      threadId: options.threadId || undefined,
      model: options.model,
    }),
    signal: options.signal,
  })

  if (!response.ok) throw new Error(await reason(response))
  if (!response.body) throw new Error('The answer never started')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result: SendResult | null = null
  /** Where the answer is being written, once the server has said (`start`). */
  let startedProject = options.projectId ?? ''
  let startedThread: string | null = options.threadId && options.threadId !== 'new' ? options.threadId : null

  /*
   * A LINE THAT GOES QUIET IS A LINE THAT IS GONE (2026-11-03).
   *
   * Turns on Sol take 30 to 70 seconds, and the connection sometimes dropped
   * on the way without ever closing: the page sat on "Searching the web..."
   * while the server finished and saved the answer. The server sends a
   * heartbeat every ten seconds, so thirty seconds of nothing at all means the
   * line is dead -- it is let go, and the answer fetched from where the server
   * keeps it (recoverAnswer below).
   */
  let lastHeard = Date.now()
  let silent = false
  const watchdog = setInterval(() => {
    if (Date.now() - lastHeard > SILENT_MS) {
      silent = true
      void reader.cancel().catch(() => undefined)
    }
  }, 5_000)

  try {
  for (;;) {
    let chunk: ReadableStreamReadResult<Uint8Array>
    try {
      chunk = await reader.read()
    } catch (error) {
      // Dropped on the way: looked for below, unless the person stopped it.
      if (options.signal?.aborted) throw error
      break
    }
    const { done, value } = chunk
    if (done) break
    lastHeard = Date.now()
    buffer += decoder.decode(value, { stream: true })

    // Events are separated by a blank line; whatever follows the last one is
    // half an event and waits for the next chunk.
    const chunks = buffer.split('\n\n')
    buffer = chunks.pop() ?? ''

    for (const chunk of chunks) {
      const line = chunk.split('\n').find((entry) => entry.startsWith('data:'))
      if (!line) continue

      let event: {
        type?: string
        text?: string
        message?: string
        projectId?: string
        threadId?: string | null
        model?: string
        messageId?: string
        runId?: string
        count?: unknown
        runs?: unknown
        reason?: string
      }
      try {
        event = JSON.parse(line.slice(5).trim())
      } catch {
        continue
      }

      if (event.type === 'start') {
        if (event.projectId) {
          startedProject = event.projectId
          startedThread = typeof event.threadId === 'string' ? event.threadId : null
          options.onStart?.(event.projectId, startedThread)
        }
      } else if (event.type === 'delta' && typeof event.text === 'string') {
        options.onDelta(event.text)
      } else if (event.type === 'searching') {
        options.onSearching?.()
      } else if (event.type === 'thinking' && typeof event.text === 'string') {
        options.onThinking?.(event.text)
      } else if (event.type === 'picture-start') {
        options.onPictureStart?.()
      } else if (event.type === 'picture-queued' && typeof event.runId === 'string') {
        options.onPictureQueued?.(event.runId, Math.max(1, Number(event.count) || 1))
      } else if (event.type === 'picture-failed') {
        options.onPictureFailed?.(
          typeof event.reason === 'string' ? event.reason : 'It could not be started',
        )
      } else if (event.type === 'error') {
        throw new Error(event.message || 'The model could not be reached')
      } else if (event.type === 'done') {
        result = {
          projectId: event.projectId ?? '',
          model: event.model ?? '',
          messageId: typeof event.messageId === 'string' ? event.messageId : '',
          runs: Array.isArray(event.runs)
            ? (event.runs as Record<string, unknown>[])
                .map((one) => ({
                  id: String(one?.id ?? ''),
                  n: Math.max(1, Number(one?.n) || 1),
                }))
                .filter((one) => one.id !== '')
            : [],
        }
      }
    }
  }

  } finally {
    clearInterval(watchdog)
  }

  // A stream that ends without saying so is a stream that was cut off. The
  // server goes on and saves the answer whether anybody is listening or not,
  // so it is looked for there before the page is told it was lost.
  if (!result && startedProject && !options.signal?.aborted) {
    const found = await recoverAnswer(startedProject, startedThread, options.message, options.signal)
    if (found) {
      options.onRecovered?.(found.answer, found.images)
      return found.result
    }
  }
  if (!result) throw new Error(silent ? 'The connection went quiet and the answer could not be found' : 'The answer was cut off')
  return result
}

/**
 * The answer to `message`, read back from the conversation as the server
 * saved it: the first assistant message after the last time `message` was
 * sent. Looked for every few seconds for a few minutes -- the server may
 * still be writing it -- and null if it never appears.
 */
async function recoverAnswer(
  projectId: string,
  threadId: string | null,
  message: string,
  signal?: AbortSignal,
): Promise<{ answer: string; images: ImageRef[]; result: SendResult } | null> {
  const until = Date.now() + RECOVER_FOR_MS
  const sent = message.trim()
  while (Date.now() < until && !signal?.aborted) {
    try {
      const conversation = await readConversation(projectId, threadId)
      const list = conversation.messages
      let asked = -1
      for (let i = list.length - 1; i >= 0; i -= 1) {
        if (list[i]!.role === 'user' && list[i]!.body.trim() === sent) {
          asked = i
          break
        }
      }
      const reply = asked >= 0 ? list.slice(asked + 1).find((one) => one.role === 'assistant') : undefined
      if (reply) {
        return {
          answer: reply.body,
          images: reply.images ?? [],
          result: {
            projectId,
            model: reply.model,
            messageId: reply.id,
            runs: conversation.pending
              .filter((one) => one.messageId === reply.id)
              .map((one) => ({ id: one.runId, n: one.n })),
          },
        }
      }
    } catch (error) {
      console.warn('looking for the saved answer', error)
    }
    await new Promise((resolve) => setTimeout(resolve, RECOVER_EVERY_MS))
  }
  return null
}

/* ---------- Sharing a project ---------- */

export interface Colleague {
  id: string
  name: string
  email: string
}

/** People with a WonderWorks account, by name or address. Not yourself. */
export function findPeople(query: string): Promise<Colleague[]> {
  return get<Colleague[]>(`/api/people?q=${encodeURIComponent(query.trim())}`)
}

export interface ShareResult {
  shared: { id: string; name: string; projectId: string }[]
  failed: { id: string; name: string; reason: string }[]
}

/**
 * Give each of these people their own copy of the project, as it is now.
 * See workers/api/src/routes/share.ts.
 */
export async function shareProject(
  projectId: string,
  people: string[],
  /** The card's colour and background picture, as the person sharing sees them (2026-10-26). */
  look?: { colour?: string; picture?: string } | null,
): Promise<ShareResult> {
  const response = await fetch(`${API_BASE}/api/projects/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders('web')) },
    body: JSON.stringify({ projectId, people, ...(look ? { look } : {}) }),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as Partial<ShareResult>
  return { shared: body.shared ?? [], failed: body.failed ?? [] }
}
